<template>
  <validation-observer ref="leadDistributionObserver">
    <div class="modal-card">
      <header class="modal-card-head">
        <h2 v-if="mode === 'leadDistributionSet'" class="modal-card-title">
          {{ leadDistributionSet ? 'Edit set' : 'New set' }}
        </h2>
        <h2 v-if="mode === 'leadDistributionOwner'" class="modal-card-title">
          Lead owner
        </h2>
        <h2 v-if="mode === 'leadSourceAssign'" class="modal-card-title">
          Assign lead sources
        </h2>
        <button class="modal-close is-large" @click="$emit('close')"></button>
      </header>
      <section class="modal-card-body">
        <lead-distribution-set v-if="mode === 'leadDistributionSet'"
                               ref="leadDistributionSet"
                               :checked-rows="checkedRows"
                               :lead-distribution-set="leadDistributionSet"
                               v-model.trim="newTitle"
                               :blur="() => { trimSpaces('newTitle') }"
                               @checking-row="checkingRow"
                               :data="data">
        </lead-distribution-set>

        <lead-distribution-owner v-if="mode === 'leadDistributionOwner'"
                                 ref="leadDistributionOwner"
                                 v-model="data"
                                 :lead-distribution-set="leadDistributionSet"
                                 :checked-rows="checkedRows"
                                 @checking-row="checkingRow"
                                 @percent-changed="percentChanged"
                                 >
        </lead-distribution-owner>

        <lead-source-assign v-if="mode === 'leadSourceAssign'"
                            ref="leadSourceAssign"
                            v-model="data"
                            :lead-source-assign="leadSourceAssign"
                            :selected-rows="selectedRows"
                            :data="data"
                            :dataLeadDistribution="dataLeadDistribution">
        </lead-source-assign>
      </section>
      <footer class="modal-card-foot buttons is-right"
              :class="{'padding-top-0': mode === 'leadDistributionOwner'}">
        <b-field v-if="mode === 'leadDistributionOwner'"
                 class="footer-lead-distribution-owner-allocate-info">
          <p :class="{'isDanger':submitOverDistributed}"
             v-html="getDistributionInfo()">{{getDistributionInfo()}}</p>
        </b-field>
        <div>
          <button class="button button-cancel" type="button" @click="cancel">Cancel</button>
          <button class="button is-success"
                  @click="submit">submit</button>
        </div>
      </footer>
    </div>
  </validation-observer>
</template>

<script>
import _ from 'lodash';
import { mapMutations } from 'vuex';
import { postLeadDistribution, postLeadDistributionOwners, assignLeadSourcesToSets } from '@/api/settings';
import LeadDistributionSet from '@/components/LeadDistributionSet.vue';
import LeadDistributionOwner from '@/components/LeadDistributionOwner.vue';
import LeadSourceAssign from '@/components/LeadSourceAssign.vue';
import * as MUTATIONS from '../../store/mutations';

export default {
  name: 'LeadDistribution',
  components: { LeadDistributionSet, LeadDistributionOwner, LeadSourceAssign },
  props: ['isActive', 'data', 'leadDistributionSet', 'mode', 'leadSourceAssign', 'dataLeadDistribution'],
  mounted() {
    if (this.leadDistributionSet) {
      let array = [];
      if (this.mode === 'leadDistributionSet') {
        array = this.leadDistributionSet.leadSources;
      } else if (this.mode === 'leadDistributionOwner') {
        array = this.leadDistributionSet.leadOwners;
      }
      array.forEach((el) => {
        const idx = this.data.findIndex((elem) => elem.id === el.id);
        if (idx > -1) {
          this.checkedRows.push(this.data[idx]);
        }
      });
    }
  },
  data() {
    return {
      newTitle: this.leadDistributionSet ? this.leadDistributionSet.title : '',
      checkboxPosition: 'left',
      checkedRows: [],
      selectedRows: [],
      submitFn: null,
      notDistributed: null,
      submitOverDistributed: false,
    };
  },
  methods: {
    ...mapMutations({ preloader: MUTATIONS.SET_PRELOADER }),

    percentChanged() {
      this.submitOverDistributed = false;
      this.getDistributionInfo();
    },
    checkingRow(rows) {
      this.checkedRows = rows;
      if (this.mode === 'leadDistributionOwner') {
        this.submitOverDistributed = false;
      }
    },
    async submit() {
      const valid = await this.$refs.leadDistributionObserver.validate();
      if (!valid) {
        const { setNameField } = this.$refs.leadDistributionSet.$refs;
        const input = setNameField.$el.querySelector('input.input');
        input.focus();
        this.modalCardBodyToTop();
        return;
      }
      this.preloader(true);
      if (this.mode === 'leadDistributionSet') {
        if (this.checkedRows.length < 1) {
          this.$refs.leadDistributionSet.isNoOneLeadSourceSelected = true;
          this.modalCardBodyToTop();
          this.preloader(false);
          return;
        }
        this.submitFn = () => (this.leadDistributionSet ? postLeadDistribution({
          id: this.leadDistributionSet.id,
          title: this.newTitle,
          leadSources: this.checkedRows,
        }) : postLeadDistribution({
          title: this.newTitle,
          leadSources: this.checkedRows,
        }));
      }
      if (this.mode === 'leadDistributionOwner') {
        if (this.notDistributed !== 0) {
          this.submitOverDistributed = true;
          this.preloader(false);
          return;
        }
        this.submitFn = () => postLeadDistributionOwners({
          id: this.leadDistributionSet.id,
          leadOwners: this.checkedRows,
        });
      }
      if (this.mode === 'leadSourceAssign') {
        if (!this.selectedRows.leadSources || this.selectedRows.leadSources.length < 1) {
          this.$emit('close');
          this.preloader(false);
          return;
        }
        this.submitFn = () => assignLeadSourcesToSets({
          leadSources: this.selectedRows.leadSources,
        });
      }

      this.submitFn().then(() => {
        this.$emit('submit', { context: this });
        this.$emit('close');
        this.$buefy.toast.open({ message: 'Successful', type: 'is-success', duration: 3000 });
      }).catch((err) => {
        const error = _.get(err, 'response.data.error', null);
        const message = _.get(err, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com');
        if (error) {
          this.$buefy.toast.open({ message, type: 'is-danger', duration: 3000 });
        } else {
          this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com' });
        }
      }).finally(() => this.preloader(false));
    },
    cancel() {
      this.$emit('close');
    },
    trimSpaces(field) {
      this[field] = this[field].replace(/([\s]+)/gu, ' ').replace(/^[\s]|[\s]$/gu, '');
    },
    modalCardBodyToTop() {
      const cardBody = document.querySelector('.modal-card-body');
      if (cardBody) cardBody.scrollTop = 0;
    },
    getDistributionInfo() {
      this.notDistributed = 100 - this.getDistributed();
      let info = '';
      if (this.notDistributed === 100) {
        info = 'Please distribute the leads among lead owners';
      } else if (this.notDistributed > 0 && this.notDistributed < 100) {
        info = `<b>${100 - this.notDistributed}%</b> of leads are distributed,
          please distribute the rest <b>${this.notDistributed}%</b>`;
      } else if (this.notDistributed === 0) {
        info = '100% of leads are distributed';
      } else if (this.notDistributed < 0) {
        info = 'The number of distributed leads cannot exceed 100%';
      }
      return info;
    },
    getDistributed() {
      let distributed = 0;
      this.data.forEach((elem) => {
        distributed += elem.percent ? Number(elem.percent) : 0;
      });
      return distributed;
    },
  },
};
</script>

<style scoped lang="stylus">
button.button
  width 135px
  height 46px
  border 1px solid #23D160
  font-size 19px
  text-transform uppercase
.button.button-cancel
  color #23D160
.button.button-cancel:hover
  background #17b548
  color white
.button.is-success
  border none
.isDanger
  color #DE4B4B
.padding-top-0
  padding-top 10px
.footer-lead-distribution-owner-allocate-info
  width 100%
.is-checked .span-used-distribution-set
  text-decoration line-through

  .field >>> .label {
    margin-bottom: 0;
  }
  .field >>> .help {
    margin-top: 0;
  }
.modal-card .modal-card-title
  font-weight: 600;
  margin-bottom unset

  .buttons:last-child {
    margin-bottom: unset;
  }

  .field >>> .textarea {
    padding: 0;
  }

  .modal-card-body {
    padding: 0 20px;
    max-height: 800px;
  }

</style>
